<template>
  <v-dialog :model-value="isOpen" max-width="500" persistent>
    <v-card class="rounded-lg" elevation="8" style="display: flex; flex-direction: column; min-height: 480px;">
      <!-- Header -->
      <div class="dialog-header d-flex align-center">
        <v-icon color="var(--dark-2)" size="22" class="mr-2">mdi-cancel</v-icon>
        <span class="dialog-title flex-grow-1">Cancel Subscription</span>
        <v-btn icon variant="text" @click="close" color="grey-darken-1">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>
      <v-divider></v-divider>

      <!-- Main Content -->
      <div class="dialog-content flex-grow-1 d-flex flex-column justify-space-between">
        <transition name="fade" mode="out-in">
          <div v-if="!showOffboarding" key="main" class="pa-6 d-flex flex-column" style="gap: 28px;">
            <!-- What happens if you cancel -->
            <div class="info-section">
              <div class="font-weight-medium mb-2" style="font-size: 15px;">
                <v-icon size="18" color="var(--dark-2)" class="mr-1">mdi-information-outline</v-icon>
                What happens if you cancel?
              </div>
              <ul class="info-list">
                <li>
                  <strong>Access:</strong>
                  <span class="ml-1">You keep full access until <span class="highlight">{{ nextBillingDate }}</span>.</span>
                </li>
                <li>
                  <strong>Data:</strong>
                  <span class="ml-1">Your data is retained for <span class="highlight">6 months</span> after cancellation.</span>
                </li>
                <li>
                  <strong>Projects:</strong>
                  <span class="ml-1">All users on your plan will lose access after cancellation.</span>
                </li>
              </ul>
            </div>
            <!-- Current Plan -->
            <div class="plan-section d-flex align-center">
              <v-icon size="18" color="var(--dark-2)" class="mr-1">mdi-cube-outline</v-icon>
              <div>
                <div class="plan-label">Current Plan</div>
                <div class="plan-value font-weight-medium highlight">{{ selectedPlan?.name }}</div>
              </div>
            </div>
            <!-- Next Payment -->
            <div class="plan-section d-flex align-center">
              <v-icon size="18" color="var(--dark-2)" class="mr-1">mdi-currency-usd</v-icon>
              <div>
                <div class="plan-label">Next Payment</div>
                <div class="plan-value">
                  <span class="font-weight-medium highlight">${{ scheduledNextBillingAmount }}</span>
                  <span>on <span class="highlight">{{ nextBillingDate }}</span></span>
                </div>
              </div>
            </div>
          </div>

          <!-- Offboarding -->
          <div v-else-if="!showFeedbackThanks" key="offboarding" class="pa-6 d-flex flex-column" style="gap: 24px;">
            <div>
              <div class="text-h6 font-weight-bold mb-2 d-flex align-center" style="font-size: 18px;">
                <v-icon color="var(--dark-2)" class="mr-2">mdi-emoticon-sad-outline</v-icon>
                We're sorry to see you go!
              </div>
              <div class="mb-3" style="font-size: 14px;">Please tell us why you're canceling (select all that apply):</div>
              <div class="reason-list mb-2">
                <div
                  v-for="option in reasons"
                  :key="option"
                  class="reason-item d-flex align-center"
                  @click="toggleReason(option)"
                  :class="{ selected: selectedReasons.includes(option) }"
                  style="cursor: pointer;"
                >
                  <v-icon
                    size="22"
                    :color="selectedReasons.includes(option) ? 'var(--blue-3)' : 'grey-lighten-1'"
                    class="mr-2"
                  >
                    {{ selectedReasons.includes(option) ? 'mdi-check-circle' : 'mdi-checkbox-blank-circle-outline' }}
                  </v-icon>
                  <span class="reason-label">{{ option }}</span>
                </div>
              </div>
              <v-textarea
                v-model="feedback"
                label="Additional feedback"
                rows="2"
                auto-grow
                variant="outlined"
                class="mt-2"
                :counter="200"
                :rules="[feedbackRule]"
                style="font-size: 14px;"
                hide-details="auto"
                :disabled="selectedReasons.length === 0"
              />
            </div>
          </div>
          <div v-else key="thanks" class="pa-6 d-flex flex-column align-center justify-center" style="min-height: 240px;">
            <v-icon color="var(--blue-3)" size="38" class="mb-3">mdi-check-circle-outline</v-icon>
            <div class="text-center" style="font-size: 17px; font-weight: 500; color: var(--dark-4);">
              Thanks for your feedback.<br>
              Your input helps us improve Continuum Design for you and others.
            </div>
          </div>
        </transition>
      </div>

      <!-- Footer Buttons -->
      <div class="dialog-footer d-flex justify-end gap-2 pa-2">
        <template v-if="!showOffboarding">
          <v-btn
            color="primary"
            variant="tonal"
            @click="onChangeSubscription"
            :disabled="loadingCancelSubscription"
            size="large"
          >
            Change Plan
          </v-btn>
          <v-btn
            color="var(--red-1)"
            variant="tonal"
            @click="showOffboarding = true"
            size="large"
          >
            Continue
          </v-btn>
        </template>
        <template v-else>
          <v-btn
            variant="tonal"
            color="primary"
            @click="showOffboarding = false"
            size="large"
          >
            Back
          </v-btn>
          <v-btn
            color="var(--red-1)"
            variant="tonal"
            :loading="loadingCancelSubscription"
            :disabled="selectedReasons.length === 0 || (feedback || '').trim().length < 20"
            @click="submitOffboarding"
            size="large"
          >
            Cancel my subscription
          </v-btn>
        </template>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import store from '@/store'
import format from 'date-fns/format'
import { mapState } from 'vuex'
import { getPlanByPriceId, getSubscriptionTypeByPriceId } from '@/utils/payments'

const MIN_FEEDBACK_LENGTH = 20

export default {
  name: 'cancel-subscription-dialog',
  props: {
    isOpen: {
      type: Boolean,
      required: true,
      default: false
    }
  },
  data () {
    return {
      showOffboarding: false,
      showFeedbackThanks: false,
      reasons: [
        'Too expensive',
        'Missing features I need',
        'Found a better alternative',
        'Not using Continuum Design enough',
        'Bugs or technical issues',
        'Just exploring',
        'Other'
      ],
      selectedReasons: [],
      feedback: ''
    }
  },
  computed: {
    ...mapState({
      activeSubscription: state => state.payments.activeSubscription,
      subscriptionUsers: state => state.payments.subscriptionUsers,
      loadingCancelSubscription: state => state.payments.loadingCancelSubscription
    }),
    selectedPlan () {
      if (this.activeSubscription) {
        return getPlanByPriceId(this.activeSubscription?.subscriptionDetails?.price?.id)
      }
      return null
    },
    nextBillingDate () {
      if (this.activeSubscription?.subscriptionDetails?.nextBillingDate) {
        return format(new Date(this.activeSubscription?.subscriptionDetails?.nextBillingDate), 'MMMM dd, yyyy')
      }
      return ''
    },
    feedbackRule () {
      return v =>
        (this.selectedReasons.length === 0 || (v && v.trim().length >= MIN_FEEDBACK_LENGTH)) ||
        'Please enter at least 20 characters.'
    },
    scheduledPriceId: function () {
      return this.activeSubscription?.scheduleSubscriptionDetails?.phases?.[0]?.items?.[0]?.priceId || null
    },
    scheduledSubscriptionType: function () {
      if (this.scheduledPriceId) {
        return getSubscriptionTypeByPriceId(this.scheduledPriceId)
      }
      return null
    },
    scheduledPlan: function () {
      if (this.scheduledPriceId) {
        return getPlanByPriceId(this.scheduledPriceId)
      }
      return null
    },
    scheduledNextBillingAmount: function () {
      const scheduleQuantity = this.activeSubscription?.scheduleSubscriptionDetails?.phases?.[0]?.items?.[0]?.quantity
      if (this.scheduledPlan) {
        const subscriptionType = this.scheduledSubscriptionType
        const validQuantity = typeof scheduleQuantity === 'number' && !isNaN(scheduleQuantity) ? scheduleQuantity : 0
        return validQuantity * (subscriptionType === 'monthly' ? this.scheduledPlan.monthlyPrice : this.scheduledPlan.yearlyPrice)
      }

      if (this.selectedPlan && this.subscriptionUsers) {
        const priceId = this.activeSubscription?.subscriptionDetails?.price?.id || this.activeSubscription?.lastSubscriptionDetails?.price?.id
        const currentSubscriptionType = getSubscriptionTypeByPriceId(priceId)
        const price = currentSubscriptionType === 'monthly'
          ? this.selectedPlan.monthlyPrice
          : this.selectedPlan.yearlyPrice
        return price * this.subscriptionUsers.length
      }

      return 0
    }
  },
  methods: {
    close () {
      this.$emit('close')
    },
    async onCancelSubscription () {
      const data = await store.dispatch('payments/cancelSubscription', {
        feedback: JSON.stringify({
          reasons: this.selectedReasons,
          message: this.feedback
        })
      })
      if (data.success) {
        this.showFeedbackThanks = true // Show thanks message
        setTimeout(() => this.$emit('onCancel'), 2000) // Optionally close after 2s
      }
    },
    onChangeSubscription () {
      this.$emit('onChangeSubscription')
    },
    submitOffboarding () {
      this.onCancelSubscription()
    },
    toggleReason (option) {
      const idx = this.selectedReasons.indexOf(option)
      if (idx === -1) {
        this.selectedReasons.push(option)
      } else {
        this.selectedReasons.splice(idx, 1)
      }
    }
  }
}
</script>

<style scoped>
.dialog-header {
  background: var(--light-2, #F5F2F0);
  color: var(--dark-4, #363A40);
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  padding: 0 0 0 12px;
  min-height: 48px;
  font-size: 18px;
  font-weight: 700;
  align-items: center;
}
.dialog-title {
  font-size: 18px;
  font-weight: 700;
  color: var(--dark-4, #363A40);
}
.dialog-content {
  flex: 1 1 auto;
  min-height: 0;
}
.dialog-footer {
  border-top: 1px solid var(--light-2, #F5F2F0);
  background: var(--light-1);
}
.info-section {
  padding-bottom: 0;
}
.info-list {
  margin: 0;
  padding-left: 24px;
  font-size: 14px;
  color: var(--dark-4, #363A40);
  list-style-type: disc;
}
.info-list li {
  margin-bottom: 6px;
}
.plan-section {
  margin-bottom: 0;
}
.plan-label {
  font-size: 13px;
  color: var(--dark-2, #878F99);
  margin-bottom: 2px;
}
.plan-value {
  font-size: 15px;
  color: var(--dark-4, #363A40);
  display: flex;
  align-items: center;
  gap: 4px;
}
.highlight {
  color: var(--blue-3, #1976d2);
  font-weight: 600;
}
.gap-2 {
  gap: 8px;
}
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.25s;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}
.reason-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.reason-item {
  transition: background 0.15s;
  border-radius: 6px;
  padding: 4px 0 4px 2px;
}
.reason-item.selected .reason-label {
  font-weight: 500;
  color: var(--blue-3, #1976d2);
}
.reason-label {
  font-size: 14px;
  color: var(--dark-4, #363A40);
}
.cancel-reason-checkbox {
  font-size: 14px;
}
</style>
